import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Pagination from '../components/Pagination';
import EmailInput from '../components/EmailInput';
import bankBranchData from '../utils/bank_branch.json';
import { downloadRecruiterTemplate } from '../utils/csvUtils';
import ReportsSection from '../components/ReportsSection';
import { QRCodeSVG } from 'qrcode.react';
import LettersManagement from '../components/LettersManagement';
import SendReferralCodeEmails from './SendReferralCodeEmails';
import HRAnalyticsDashboard from '../components/HRAnalyticsDashboard';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';

const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers
    },
    ...options
  };

  try {
    const response = await fetch(`${API_BASE}${endpoint}`, config);

    if (response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      throw new Error('Session expired');
    }

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch (parseError) {
        // Use default error message
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return { data };
  } catch (error) {
    if (error.message === 'Session expired') throw error;
    throw error;
  }
};

const hrAPI = {
  getStats: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/hr/dashboard/stats${query ? '?' + query : ''}`);
  },
  getApplications: (params) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiCall(`/hr/applications${queryParams ? '?' + queryParams : ''}`);
  },
  getApplication: (id) => apiCall(`/hr/applications/${id}`),
  reviewApplication: (id, data) => apiCall(`/hr/applications/${id}/review`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  onboardRecruiter: (id) => apiCall(`/hr/applications/${id}/onboard`, {
    method: 'POST'
  }),
  deleteApplication: (id) => apiCall(`/hr/applications/${id}`, {
    method: 'DELETE'
  }),
  archiveApplication: (id, is_archived) => apiCall(`/hr/applications/${id}/archive`, {
    method: 'PUT',
    body: JSON.stringify({ is_archived })
  }),
  getRecruiters: (params) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiCall(`/recruiters${queryParams ? '?' + queryParams : ''}`);
  },
  getRecruiter: (id) => apiCall(`/recruiters/${id}`),
  createRecruiter: (data) => apiCall('/recruiters', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  updateRecruiter: (id, data) => apiCall(`/recruiters/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  deleteRecruiter: (id) => apiCall(`/recruiters/${id}`, {
    method: 'DELETE'
  }),
  bulkUpload: (formData) => {
    const token = localStorage.getItem('token');
    return fetch(`${API_BASE}/recruiters/bulk-upload`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` })
      },
      body: formData
    }).then(response => response.json()).then(data => ({ data }));
  },
  getBulkUploadStatus: (jobId) => {
    const token = localStorage.getItem('token');
    return apiCall(`/recruiters/bulk-upload/status/${jobId}`);
  }
};

const countriesData = [
  { code: 'AF', name: 'Afghanistan', flag: '🇦🇫', dialCode: '+93' },
  { code: 'AL', name: 'Albania', flag: '🇦🇱', dialCode: '+355' },
  { code: 'DZ', name: 'Algeria', flag: '🇩🇿', dialCode: '+213' },
  { code: 'AD', name: 'Andorra', flag: '🇦🇩', dialCode: '+376' },
  { code: 'AO', name: 'Angola', flag: '🇦🇴', dialCode: '+244' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷', dialCode: '+54' },
  { code: 'AM', name: 'Armenia', flag: '🇦🇲', dialCode: '+374' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', dialCode: '+61' },
  { code: 'AT', name: 'Austria', flag: '🇦🇹', dialCode: '+43' },
  { code: 'AZ', name: 'Azerbaijan', flag: '🇦🇿', dialCode: '+994' },
  { code: 'BH', name: 'Bahrain', flag: '🇧🇭', dialCode: '+973' },
  { code: 'BD', name: 'Bangladesh', flag: '🇧🇩', dialCode: '+880' },
  { code: 'BY', name: 'Belarus', flag: '🇧🇾', dialCode: '+375' },
  { code: 'BE', name: 'Belgium', flag: '🇧🇪', dialCode: '+32' },
  { code: 'BZ', name: 'Belize', flag: '🇧🇿', dialCode: '+501' },
  { code: 'BJ', name: 'Benin', flag: '🇧🇯', dialCode: '+229' },
  { code: 'BT', name: 'Bhutan', flag: '🇧🇹', dialCode: '+975' },
  { code: 'BO', name: 'Bolivia', flag: '🇧🇴', dialCode: '+591' },
  { code: 'BA', name: 'Bosnia and Herzegovina', flag: '🇧🇦', dialCode: '+387' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷', dialCode: '+55' },
  { code: 'BN', name: 'Brunei', flag: '🇧🇳', dialCode: '+673' },
  { code: 'BG', name: 'Bulgaria', flag: '🇧🇬', dialCode: '+359' },
  { code: 'BF', name: 'Burkina Faso', flag: '🇧🇫', dialCode: '+226' },
  { code: 'BI', name: 'Burundi', flag: '🇧🇮', dialCode: '+257' },
  { code: 'KH', name: 'Cambodia', flag: '🇰🇭', dialCode: '+855' },
  { code: 'CM', name: 'Cameroon', flag: '🇨🇲', dialCode: '+237' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', dialCode: '+1' },
  { code: 'CV', name: 'Cape Verde', flag: '🇨🇻', dialCode: '+238' },
  { code: 'CF', name: 'Central African Republic', flag: '🇨🇫', dialCode: '+236' },
  { code: 'TD', name: 'Chad', flag: '🇹🇩', dialCode: '+235' },
  { code: 'CL', name: 'Chile', flag: '🇨🇱', dialCode: '+56' },
  { code: 'CN', name: 'China', flag: '🇨🇳', dialCode: '+86' },
  { code: 'CO', name: 'Colombia', flag: '🇨🇴', dialCode: '+57' },
  { code: 'KM', name: 'Comoros', flag: '🇰🇲', dialCode: '+269' },
  { code: 'CG', name: 'Congo', flag: '🇨🇬', dialCode: '+242' },
  { code: 'CR', name: 'Costa Rica', flag: '🇨🇷', dialCode: '+506' },
  { code: 'HR', name: 'Croatia', flag: '🇭🇷', dialCode: '+385' },
  { code: 'CU', name: 'Cuba', flag: '🇨🇺', dialCode: '+53' },
  { code: 'CY', name: 'Cyprus', flag: '🇨🇾', dialCode: '+357' },
  { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿', dialCode: '+420' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰', dialCode: '+45' },
  { code: 'DJ', name: 'Djibouti', flag: '🇩🇯', dialCode: '+253' },
  { code: 'DM', name: 'Dominica', flag: '🇩🇲', dialCode: '+1767' },
  { code: 'DO', name: 'Dominican Republic', flag: '🇩🇴', dialCode: '+1809' },
  { code: 'EC', name: 'Ecuador', flag: '🇪🇨', dialCode: '+593' },
  { code: 'EG', name: 'Egypt', flag: '🇪🇬', dialCode: '+20' },
  { code: 'SV', name: 'El Salvador', flag: '🇸🇻', dialCode: '+503' },
  { code: 'GQ', name: 'Equatorial Guinea', flag: '🇬🇶', dialCode: '+240' },
  { code: 'ER', name: 'Eritrea', flag: '🇪🇷', dialCode: '+291' },
  { code: 'EE', name: 'Estonia', flag: '🇪🇪', dialCode: '+372' },
  { code: 'ET', name: 'Ethiopia', flag: '🇪🇹', dialCode: '+251' },
  { code: 'FJ', name: 'Fiji', flag: '🇫🇯', dialCode: '+679' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮', dialCode: '+358' },
  { code: 'FR', name: 'France', flag: '🇫🇷', dialCode: '+33' },
  { code: 'GA', name: 'Gabon', flag: '🇬🇦', dialCode: '+241' },
  { code: 'GM', name: 'Gambia', flag: '🇬🇲', dialCode: '+220' },
  { code: 'GE', name: 'Georgia', flag: '🇬🇪', dialCode: '+995' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', dialCode: '+49' },
  { code: 'GH', name: 'Ghana', flag: '🇬🇭', dialCode: '+233' },
  { code: 'GR', name: 'Greece', flag: '🇬🇷', dialCode: '+30' },
  { code: 'GD', name: 'Grenada', flag: '🇬🇩', dialCode: '+1473' },
  { code: 'GT', name: 'Guatemala', flag: '🇬🇹', dialCode: '+502' },
  { code: 'GN', name: 'Guinea', flag: '🇬🇳', dialCode: '+224' },
  { code: 'GW', name: 'Guinea-Bissau', flag: '🇬🇼', dialCode: '+245' },
  { code: 'GY', name: 'Guyana', flag: '🇬🇾', dialCode: '+592' },
  { code: 'HT', name: 'Haiti', flag: '🇭🇹', dialCode: '+509' },
  { code: 'HN', name: 'Honduras', flag: '🇭🇳', dialCode: '+504' },
  { code: 'HK', name: 'Hong Kong', flag: '🇭🇰', dialCode: '+852' },
  { code: 'HU', name: 'Hungary', flag: '🇭🇺', dialCode: '+36' },
  { code: 'IS', name: 'Iceland', flag: '🇮🇸', dialCode: '+354' },
  { code: 'IN', name: 'India', flag: '🇮🇳', dialCode: '+91' },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩', dialCode: '+62' },
  { code: 'IR', name: 'Iran', flag: '🇮🇷', dialCode: '+98' },
  { code: 'IQ', name: 'Iraq', flag: '🇮🇶', dialCode: '+964' },
  { code: 'IE', name: 'Ireland', flag: '🇮🇪', dialCode: '+353' },
  { code: 'IL', name: 'Israel', flag: '🇮🇱', dialCode: '+972' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹', dialCode: '+39' },
  { code: 'JM', name: 'Jamaica', flag: '🇯🇲', dialCode: '+1876' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵', dialCode: '+81' },
  { code: 'JO', name: 'Jordan', flag: '🇯🇴', dialCode: '+962' },
  { code: 'KZ', name: 'Kazakhstan', flag: '🇰🇿', dialCode: '+7' },
  { code: 'KE', name: 'Kenya', flag: '🇰🇪', dialCode: '+254' },
  { code: 'KI', name: 'Kiribati', flag: '🇰🇮', dialCode: '+686' },
  { code: 'KP', name: 'North Korea', flag: '🇰🇵', dialCode: '+850' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷', dialCode: '+82' },
  { code: 'KW', name: 'Kuwait', flag: '🇰🇼', dialCode: '+965' },
  { code: 'KG', name: 'Kyrgyzstan', flag: '🇰🇬', dialCode: '+996' },
  { code: 'LA', name: 'Laos', flag: '🇱🇦', dialCode: '+856' },
  { code: 'LV', name: 'Latvia', flag: '🇱🇻', dialCode: '+371' },
  { code: 'LB', name: 'Lebanon', flag: '🇱🇧', dialCode: '+961' },
  { code: 'LS', name: 'Lesotho', flag: '🇱🇸', dialCode: '+266' },
  { code: 'LR', name: 'Liberia', flag: '🇱🇷', dialCode: '+231' },
  { code: 'LY', name: 'Libya', flag: '🇱🇾', dialCode: '+218' },
  { code: 'LI', name: 'Liechtenstein', flag: '🇱🇮', dialCode: '+423' },
  { code: 'LT', name: 'Lithuania', flag: '🇱🇹', dialCode: '+370' },
  { code: 'LU', name: 'Luxembourg', flag: '🇱🇺', dialCode: '+352' },
  { code: 'MG', name: 'Madagascar', flag: '🇲🇬', dialCode: '+261' },
  { code: 'MW', name: 'Malawi', flag: '🇲🇼', dialCode: '+265' },
  { code: 'MY', name: 'Malaysia', flag: '🇲🇾', dialCode: '+60' },
  { code: 'MV', name: 'Maldives', flag: '🇲🇻', dialCode: '+960' },
  { code: 'ML', name: 'Mali', flag: '🇲🇱', dialCode: '+223' },
  { code: 'MT', name: 'Malta', flag: '🇲🇹', dialCode: '+356' },
  { code: 'MR', name: 'Mauritania', flag: '🇲🇷', dialCode: '+222' },
  { code: 'MU', name: 'Mauritius', flag: '🇲🇺', dialCode: '+230' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽', dialCode: '+52' },
  { code: 'MD', name: 'Moldova', flag: '🇲🇩', dialCode: '+373' },
  { code: 'MC', name: 'Monaco', flag: '🇲🇨', dialCode: '+377' },
  { code: 'MN', name: 'Mongolia', flag: '🇲🇳', dialCode: '+976' },
  { code: 'ME', name: 'Montenegro', flag: '🇲🇪', dialCode: '+382' },
  { code: 'MA', name: 'Morocco', flag: '🇲🇦', dialCode: '+212' },
  { code: 'MZ', name: 'Mozambique', flag: '🇲🇿', dialCode: '+258' },
  { code: 'MM', name: 'Myanmar', flag: '🇲🇲', dialCode: '+95' },
  { code: 'NA', name: 'Namibia', flag: '🇳🇦', dialCode: '+264' },
  { code: 'NR', name: 'Nauru', flag: '🇳🇷', dialCode: '+674' },
  { code: 'NP', name: 'Nepal', flag: '🇳🇵', dialCode: '+977' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱', dialCode: '+31' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', dialCode: '+64' },
  { code: 'NI', name: 'Nicaragua', flag: '🇳🇮', dialCode: '+505' },
  { code: 'NE', name: 'Niger', flag: '🇳🇪', dialCode: '+227' },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬', dialCode: '+234' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴', dialCode: '+47' },
  { code: 'OM', name: 'Oman', flag: '🇴🇲', dialCode: '+968' },
  { code: 'PK', name: 'Pakistan', flag: '🇵🇰', dialCode: '+92' },
  { code: 'PW', name: 'Palau', flag: '🇵🇼', dialCode: '+680' },
  { code: 'PS', name: 'Palestine', flag: '🇵🇸', dialCode: '+970' },
  { code: 'PA', name: 'Panama', flag: '🇵🇦', dialCode: '+507' },
  { code: 'PG', name: 'Papua New Guinea', flag: '🇵🇬', dialCode: '+675' },
  { code: 'PY', name: 'Paraguay', flag: '🇵🇾', dialCode: '+595' },
  { code: 'PE', name: 'Peru', flag: '🇵🇪', dialCode: '+51' },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭', dialCode: '+63' },
  { code: 'PL', name: 'Poland', flag: '🇵🇱', dialCode: '+48' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹', dialCode: '+351' },
  { code: 'PR', name: 'Puerto Rico', flag: '🇵🇷', dialCode: '+1787' },
  { code: 'QA', name: 'Qatar', flag: '🇶🇦', dialCode: '+974' },
  { code: 'RO', name: 'Romania', flag: '🇷🇴', dialCode: '+40' },
  { code: 'RU', name: 'Russia', flag: '🇷🇺', dialCode: '+7' },
  { code: 'RW', name: 'Rwanda', flag: '🇷🇼', dialCode: '+250' },
  { code: 'KN', name: 'Saint Kitts and Nevis', flag: '🇰🇳', dialCode: '+1869' },
  { code: 'LC', name: 'Saint Lucia', flag: '🇱🇨', dialCode: '+1758' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines', flag: '🇻🇨', dialCode: '+1784' },
  { code: 'WS', name: 'Samoa', flag: '🇼🇸', dialCode: '+685' },
  { code: 'SM', name: 'San Marino', flag: '🇸🇲', dialCode: '+378' },
  { code: 'ST', name: 'São Tomé and Príncipe', flag: '🇸🇹', dialCode: '+239' },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', dialCode: '+966' },
  { code: 'SN', name: 'Senegal', flag: '🇸🇳', dialCode: '+221' },
  { code: 'RS', name: 'Serbia', flag: '🇷🇸', dialCode: '+381' },
  { code: 'SC', name: 'Seychelles', flag: '🇸🇨', dialCode: '+248' },
  { code: 'SL', name: 'Sierra Leone', flag: '🇸🇱', dialCode: '+232' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬', dialCode: '+65' },
  { code: 'SK', name: 'Slovakia', flag: '🇸🇰', dialCode: '+421' },
  { code: 'SI', name: 'Slovenia', flag: '🇸🇮', dialCode: '+386' },
  { code: 'SB', name: 'Solomon Islands', flag: '🇸🇧', dialCode: '+677' },
  { code: 'SO', name: 'Somalia', flag: '🇸🇴', dialCode: '+252' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦', dialCode: '+27' },
  { code: 'SS', name: 'South Sudan', flag: '🇸🇸', dialCode: '+211' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸', dialCode: '+34' },
  { code: 'LK', name: 'Sri Lanka', flag: '🇱🇰', dialCode: '+94' },
  { code: 'SD', name: 'Sudan', flag: '🇸🇩', dialCode: '+249' },
  { code: 'SR', name: 'Suriname', flag: '🇸🇷', dialCode: '+597' },
  { code: 'SZ', name: 'Eswatini', flag: '🇸🇿', dialCode: '+268' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪', dialCode: '+46' },
  { code: 'CH', name: 'Switzerland', flag: '🇨🇭', dialCode: '+41' },
  { code: 'SY', name: 'Syria', flag: '🇸🇾', dialCode: '+963' },
  { code: 'TW', name: 'Taiwan', flag: '🇹🇼', dialCode: '+886' },
  { code: 'TJ', name: 'Tajikistan', flag: '🇹🇯', dialCode: '+992' },
  { code: 'TZ', name: 'Tanzania', flag: '🇹🇿', dialCode: '+255' },
  { code: 'TH', name: 'Thailand', flag: '🇹🇭', dialCode: '+66' },
  { code: 'TG', name: 'Togo', flag: '🇹🇬', dialCode: '+228' },
  { code: 'TO', name: 'Tonga', flag: '🇹🇴', dialCode: '+676' },
  { code: 'TT', name: 'Trinidad and Tobago', flag: '🇹🇹', dialCode: '+1868' },
  { code: 'TN', name: 'Tunisia', flag: '🇹🇳', dialCode: '+216' },
  { code: 'TR', name: 'Turkey', flag: '🇹🇷', dialCode: '+90' },
  { code: 'TM', name: 'Turkmenistan', flag: '🇹🇲', dialCode: '+993' },
  { code: 'UG', name: 'Uganda', flag: '🇺🇬', dialCode: '+256' },
  { code: 'UA', name: 'Ukraine', flag: '🇺🇦', dialCode: '+380' },
  { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪', dialCode: '+971' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', dialCode: '+44' },
  { code: 'US', name: 'United States', flag: '🇺🇸', dialCode: '+1' },
  { code: 'UY', name: 'Uruguay', flag: '🇺🇾', dialCode: '+598' },
  { code: 'UZ', name: 'Uzbekistan', flag: '🇺🇿', dialCode: '+998' },
  { code: 'VE', name: 'Venezuela', flag: '🇻🇪', dialCode: '+58' },
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳', dialCode: '+84' },
  { code: 'YE', name: 'Yemen', flag: '🇾🇪', dialCode: '+967' },
  { code: 'ZM', name: 'Zambia', flag: '🇿🇲', dialCode: '+260' },
  { code: 'ZW', name: 'Zimbabwe', flag: '🇿🇼', dialCode: '+263' }
];

const kenyanCountiesData = [
  { name: "Mombasa", code: 1, capital: "Mombasa City", subCounties: ["Changamwe", "Jomvu", "Kisauni", "Likoni", "Mvita", "Nyali"] },
  { name: "Kwale", code: 2, capital: "Kwale Town", subCounties: ["Kinango", "Matuga", "Msambweni"] },
  { name: "Kilifi", code: 3, capital: "Kilifi Town", subCounties: ["Ganze", "Kaloleni", "Kilifi", "Magarini", "Malindi"] },
  { name: "Tana River", code: 4, capital: "Hola", subCounties: ["Bura", "Galole", "Garsen"] },
  { name: "Lamu", code: 5, capital: "Lamu Town", subCounties: ["Amu", "Faza", "Hindi"] },
  { name: "Taita Taveta", code: 6, capital: "Mwatate", subCounties: ["Mwatate", "Taveta", "Voi"] },
  { name: "Garissa", code: 7, capital: "Garissa", subCounties: ["Balambala", "Dadaab", "Fafi", "Garissa", "Ijara", "Lagdera"] },
  { name: "Wajir", code: 8, capital: "Wajir", subCounties: ["Buna", "Eldas", "Tarbaj", "Wajir Bor", "Wajir East", "Wajir North", "Wajir South", "Wajir West"] },
  { name: "Mandera", code: 9, capital: "Mandera", subCounties: ["Banissa", "Kutulo", "Mandera East", "Mandera North", "Mandera South", "Mandera West"] },
  { name: "Marsabit", code: 10, capital: "Marsabit", subCounties: ["Laisamis", "Loiyangalani", "Marsabit Central", "Marsabit North", "Marsabit South", "North Horr"] },
  { name: "Isiolo", code: 11, capital: "Isiolo", subCounties: ["Isiolo Central", "Isiolo North", "Isiolo South", "Merti"] },
  { name: "Meru", code: 12, capital: "Meru", subCounties: ["Buuri", "Igembe Central", "Igembe North", "Igembe South", "Imenti Central", "Imenti North", "Imenti South", "Meru Central", "Meru North", "Meru South", "Tigania East", "Tigania West"] },
  { name: "Tharaka Nithi", code: 13, capital: "Kathwana", subCounties: ["Chuka", "Igambang'ombe", "Maara", "Tharaka"] },
  { name: "Embu", code: 14, capital: "Embu", subCounties: ["Embu East", "Embu North", "Embu West", "Mbeere North", "Mbeere South"] },
  { name: "Kitui", code: 15, capital: "Kitui", subCounties: ["Chuluni", "Ikutha", "Kisasi", "Kitui Central", "Kitui East", "Kitui Rural", "Kitui South", "Kyuso", "Lower Yatta", "Masinga", "Matinyani", "Migwani", "Mutomo", "Mwingi Central", "Mwingi East", "Mwingi North", "Mwingi West", "Nzambani", "Tseikuru"] },
  { name: "Machakos", code: 16, capital: "Machakos", subCounties: ["Athi River", "Kangundo", "Kathiani", "Machakos", "Masinga", "Matungulu", "Mwala", "Yatta"] },
  { name: "Makueni", code: 17, capital: "Wote", subCounties: ["Kaiti", "Kibwezi", "Kilungu", "Makindu", "Makueni", "Mbooni", "Masinga", "Mtito Andei", "Nzaui"] },
  { name: "Kajiado", code: 18, capital: "Kajiado", subCounties: ["Kajiado Central", "Kajiado East", "Kajiado North", "Kajiado South", "Kajiado West"] },
  { name: "Kiambu", code: 19, capital: "Kiambu", subCounties: ["Gatanga", "Gatundu North", "Gatundu South", "Githunguri", "Gikambura", "Juja", "Kabete", "Kikuyu", "Limuru", "Lari", "Ruiru", "Thika Town"] },
  { name: "Turkana", code: 20, capital: "Lodwar", subCounties: ["Kakuma", "Katilu", "Kibish", "Lodwar", "Loima", "Turkana Central", "Turkana East", "Turkana North", "Turkana South", "Turkana West"] },
  { name: "West Pokot", code: 21, capital: "Kapenguria", subCounties: ["Kacheliba", "Pokot Central", "Pokot North", "Pokot South"] },
  { name: "Samburu", code: 22, capital: "Maralal", subCounties: ["Baragoi", "Kirisia", "Samburu Central", "Samburu East", "Samburu North"] },
  { name: "Trans Nzoia", code: 23, capital: "Kitale", subCounties: ["Cherangany", "Endebess", "Kwanza", "Saboti", "Trans Nzoia East", "Trans Nzoia West"] },
  { name: "Uasin Gishu", code: 24, capital: "Eldoret", subCounties: ["Ainabkoi", "Kapseret", "Kesses", "Moiben", "Soy", "Turbo"] },
  { name: "Elgeyo Marakwet", code: 25, capital: "Iten", subCounties: ["Keiyo North", "Keiyo South", "Marakwet East", "Marakwet West"] },
  { name: "Nandi", code: 26, capital: "Kapsabet", subCounties: ["Aldai", "Chesumei", "Emgwen", "Kilibwoni", "Mosop", "Nandi Hills", "Tinderet"] },
  { name: "Baringo", code: 27, capital: "Kabarnet", subCounties: ["Baringo Central", "Baringo North", "Baringo South", "Baringo South", "Eldama Ravine", "Mogotio", "Tiaty"] },
  { name: "Laikipia", code: 28, capital: "Nanyuki", subCounties: ["Laikipia East", "Laikipia North", "Laikipia West"] },
  { name: "Nakuru", code: 29, capital: "Nakuru", subCounties: ["Bahati", "Gilgil", "Kuresoi North", "Kuresoi South", "Molo", "Naivasha", "Nakuru East", "Nakuru North", "Nakuru West", "Njoro", "Rongai", "Subukia"] },
  { name: "Narok", code: 30, capital: "Narok", subCounties: ["Kilgoris", "Narok East", "Narok North", "Narok South", "Narok West"] },
  { name: "Kericho", code: 31, capital: "Kericho", subCounties: ["Ainamoi", "Belgut", "Bureti", "Kipkelion East", "Kipkelion West", "Sigowet"] },
  { name: "Bomet", code: 32, capital: "Bomet", subCounties: ["Bomet East", "Bomet West", "Chepalungu", "Konoin", "Sotik"] },
  { name: "Kakamega", code: 33, capital: "Kakamega", subCounties: ["Butere", "Ikolomani", "Khwisero", "Lugari", "Lurambi", "Malava", "Mumias East", "Mumias West", "Matungu", "Shinyalu", "Shirugu"] },
  { name: "Vihiga", code: 34, capital: "Mbale", subCounties: ["Chavakali", "Emuhaya", "Hamisi", "Sabatia", "Vihiga"] },
  { name: "Bungoma", code: 35, capital: "Bungoma", subCounties: ["Bumula", "Cheptais", "Kanduyi", "Kapsokwony", "Kapsokwony", "Kimilili", "Mt Elgon", "Sirisia", "Tongaren", "Webuye East", "Webuye West"] },
  { name: "Busia", code: 36, capital: "Busia", subCounties: ["Butula", "Bunyala", "Busia", "Funyula", "Nambale", "Samia", "Teso North", "Teso South"] },
  { name: "Siaya", code: 37, capital: "Siaya", subCounties: ["Alego Usonga", "Bondo", "Gem", "Rarieda", "Siaya Town", "Ugenya", "Ukwala"] },
  { name: "Kisumu", code: 38, capital: "Kisumu", subCounties: ["Kisumu Central", "Kisumu East", "Kisumu West", "Muhoroni", "Nyando", "Nyakach"] },
  { name: "Homa Bay", code: 39, capital: "Homa Bay", subCounties: ["Gwassi", "Kasipul", "Kendu Bay", "Mbita", "Ndhiwa", "Rachuonyo", "Rangwe", "Suba"] },
  { name: "Migori", code: 40, capital: "Migori", subCounties: ["Awendo", "Kuria East", "Kuria West", "Migori", "Nyatike", "Rongo", "Suna East", "Suna West"] },
  { name: "Kisii", code: 41, capital: "Kisii", subCounties: ["Bomachoge", "Bomachoge Borabu", "Bonchari", "Gucha", "Kitutu Chache North", "Kitutu Chache South", "Marani", "Nyamache", "Nyamarambe", "Nyamira North", "Nyamira South"] },
  { name: "Nyamira", code: 42, capital: "Nyamira", subCounties: ["Borabu", "Ekerenyo", "Kitutu Masaba", "Manga", "Nyamira North", "Nyamira South"] },
  { name: "Nairobi", code: 47, capital: "Nairobi City", subCounties: ["Dagoretti North", "Dagoretti South", "Embakasi Central", "Embakasi East", "Embakasi North", "Embakasi South", "Embakasi West", "Kamukunji", "Kasarani", "Kibera", "Lang'ata", "Makadara", "Mathare", "Nairobi Central", "Nairobi West", "Roysambu", "Starehe", "Westlands"] },
  { name: "Nyandarua", code: 43, capital: "Ol Kalou", subCounties: ["Kinangop", "Ndaragwa", "Ol Kalou", "Ol Joro Orok", "Kipipiri"] },
  { name: "Nyeri", code: 44, capital: "Nyeri", subCounties: ["Kieni East", "Kieni West", "Mathira East", "Mathira West", "Mweiga", "Nyeri Central", "Nyeri South", "Tetu"] },
  { name: "Kirinyaga", code: 45, capital: "Kerugoya", subCounties: ["Gichugu", "Kirinyaga Central", "Kirinyaga East", "Kirinyaga West", "Mwea"] },
  { name: "Murang'a", code: 46, capital: "Murang'a", subCounties: ["Gatanga", "Ithanga", "Kahuro", "Kandara", "Kangema", "Kigumo", "Kiharu", "Kigumo", "Maragua", "Mathioya", "Murang'a East", "Murang'a West"] }
];

function HRDashboard() {
  return (
    <Routes>
      <Route path="/" element={<HRAnalyticsDashboard />} />
      <Route path="/applications" element={<Applications />} />
      <Route path="/applications/:id" element={<ApplicationDetails />} />
      <Route path="/recruiters" element={<HRRecruiters />} />
      <Route path="/recruiters/:id" element={<RecruiterDetails />} />
      <Route path="/reports" element={<ReportsSection title="Reports" subtitle="Generate and export reports" />} />
      <Route path="/letters" element={<LettersManagement />} />
      <Route path="/referral-code-emails" element={<SendReferralCodeEmails />} />
    </Routes>
  );
}

function HRRecruiters() {
  const navigate = useNavigate();
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20 });
  const [filters, setFilters] = useState({ search: '', county: '', category: '' });
  const [showForm, setShowForm] = useState(false);
  const [showCountyDropdown, setShowCountyDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [categories, setCategories] = useState([]);

  // Bulk upload state
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, status: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [bulkJobId, setBulkJobId] = useState(null);
  const [bulkJobDone, setBulkJobDone] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const [countyFilterSearch, setCountyFilterSearch] = useState('');

  const filteredCountiesForFilter = kenyanCountiesData.filter(county =>
    county.name.toLowerCase().includes(countyFilterSearch.toLowerCase())
  );

  const handleCountyFilterSelect = (countyName) => {
    setFilters(prev => ({ ...prev, county: countyName }));
    setCountyFilterSearch(countyName);
    setShowCountyDropdown(false);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleCategoryFilterSelect = (categoryName) => {
    setFilters(prev => ({ ...prev, category: categoryName }));
    setShowCategoryDropdown(false);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  useEffect(() => {
    apiCall('/recruiters/categories').then(res => res.json()).then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      loadRecruiters();
    }, 300);
    return () => clearTimeout(handler);
  }, [pagination.page, filters]);

  const loadRecruiters = async () => {
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };
      if (!filters.search) delete params.search;
      if (!filters.county) delete params.county;
      if (!filters.category) delete params.category;
      const response = await hrAPI.getRecruiters(params);
      setRecruiters(response.data.recruiters);
      setPagination(prev => ({ ...prev, total: response.data.pagination.total }));
    } catch (error) {
      toast.error('Failed to load recruiters');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recruiter?')) {
      return;
    }

    try {
      await hrAPI.deleteRecruiter(id);
      toast.success('Recruiter deleted successfully');
      loadRecruiters();
    } catch (error) {
      toast.error('Failed to delete recruiter');
    }
  };

  const handleView = (recruiter) => {
    navigate(`/hr/recruiters/${recruiter.id}`);
  };

  const handleFormSubmit = async (formData) => {
    try {
      await hrAPI.createRecruiter(formData);
      toast.success('Recruiter created successfully');
      setShowForm(false);
      loadRecruiters();
    } catch (error) {
      toast.error('Failed to create recruiter');
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['.csv', '.xlsx', '.xls'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validTypes.includes(fileExtension)) {
      toast.error('❌ Invalid file type. Please upload CSV or Excel files only.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('❌ File too large. Maximum size is 10MB.');
      return;
    }

    setSelectedFile(file);

    if (file.name.endsWith('.csv')) {
      const text = await file.text();
      const lines = text.split('\n').slice(0, 10);
      const preview = lines.filter(line => !line.startsWith('#')).slice(0, 5);
      setFilePreview({
        type: 'csv',
        lines: preview,
        totalLines: text.split('\n').length,
        headers: preview[0]?.split(',') || []
      });
    } else {
      setFilePreview({
        type: 'excel',
        name: file.name,
        size: (file.size / 1024).toFixed(2) + ' KB'
      });
    }

    toast.success(`📁 File "${file.name}" selected successfully. Review and click Submit to process.`);
  };

  const handleBulkSubmit = async () => {
    if (!selectedFile) {
      toast.error('❌ Please select a file first.');
      return;
    }

    setBulkUploadLoading(true);
    setBulkJobId(null);
    setBulkJobDone(false);
    setUploadProgress({ current: 0, total: 0, status: 'Uploading and validating file...' });

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await hrAPI.bulkUpload(formData);
      const { jobId, summary, errors = [], message } = response.data || {};

      const inserted = summary?.inserted || 0;
      const totalInFile = summary?.totalInFile || 0;
      const validationErrors = summary?.validationErrors || 0;
      const insertErrors = summary?.insertErrors || 0;
      const totalFailed = validationErrors + insertErrors;

      setUploadProgress({
        current: 0,
        total: inserted,
        status: inserted > 0 ? `${inserted} recruiters added. Sending emails in background...` : 'Processing complete'
      });

      if (inserted > 0) {
        toast.success(`✅ ${inserted} recruiters inserted successfully!`);
        if (totalFailed > 0) {
          toast.warning(`⚠️ ${totalFailed} rows had errors (see below).`);
        }
        setBulkJobId(jobId);
        setSelectedFile(null);
        setFilePreview(null);
        loadRecruiters();
      } else {
        toast.error(`❌ No recruiters were added. ${totalFailed} row(s) had errors.`);
        if (errors.length > 0) {
          const topErrors = errors.slice(0, 3).map(e => `Row ${e.row}: ${e.error}`).join(' | ');
          setTimeout(() => toast.error(`🔍 Errors: ${topErrors}`), 1500);
        }
      }

      if (errors.length > 0) {
        console.group('📋 Bulk Upload Row Errors');
        console.table(errors);
        console.groupEnd();
      }

    } catch (error) {
      console.error('Bulk upload error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
      toast.error('❌ Bulk upload failed: ' + errorMessage);
      setUploadProgress({ current: 0, total: 0, status: 'Upload failed' });

      if (errorMessage.includes('No valid recruiter data')) {
        setTimeout(() => toast.info('💡 Tip: Check your CSV headers match the template exactly.'), 2000);
      } else if (errorMessage.includes('Authentication required') || errorMessage.includes('Invalid token')) {
        setTimeout(() => toast.info('💡 Tip: Your session may have expired. Please refresh the page and login again.'), 2000);
      }
    } finally {
      setBulkUploadLoading(false);
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    setUploadProgress({ current: 0, total: 0, status: '' });
    setBulkJobId(null);
    setBulkJobDone(false);
  };

  // Poll for bulk upload job progress
  useEffect(() => {
    if (!bulkJobId || bulkJobDone) return;

    const interval = setInterval(async () => {
      try {
        const statusRes = await hrAPI.getBulkUploadStatus(bulkJobId);
        const job = statusRes.data;
        if (!job) return;

        setUploadProgress({
          current: job.emails_sent || 0,
          total: job.inserted || job.total || 0,
          status: `Sent ${job.emails_sent || 0} of ${job.inserted || job.total || 0} welcome emails`
        });

        if (job.status === 'done' || job.emails_sent >= job.inserted) {
          setBulkJobDone(true);
          toast.success('✅ All welcome emails sent successfully!');
          clearInterval(interval);
        }
      } catch (pollError) {
        console.error('Bulk upload polling error:', pollError);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [bulkJobId, bulkJobDone]);

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <p>Loading recruiters...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="dashboard-header">
        <h1>Recruiters</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setShowForm(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '8px 18px', borderRadius: '8px', border: 'none',
              background: '#037b90', color: '#fff', fontSize: '14px', fontWeight: 500,
              cursor: 'pointer', transition: 'background 0.15s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#025a6b'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#037b90'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/></svg>
            Add Recruiter
          </button>
          <button
            onClick={() => { downloadRecruiterTemplate(); toast.success('Recruiter template downloaded successfully!'); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '8px 18px', borderRadius: '8px',
              border: '1px solid #d1d5db', background: '#fff', color: '#374151',
              fontSize: '14px', fontWeight: 500, cursor: 'pointer', transition: 'background 0.15s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
          >
            📥 Download Template
          </button>
          <button
            onClick={() => document.getElementById('bulk-upload-input').click()}
            disabled={bulkUploadLoading}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '8px 18px', borderRadius: '8px',
              border: '1px solid #d1d5db', background: '#fff', color: '#374151',
              fontSize: '14px', fontWeight: 500, cursor: 'pointer', transition: 'background 0.15s',
              opacity: bulkUploadLoading ? 0.6 : 1
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
          >
            📤 Bulk Upload
          </button>
          <input
            id="bulk-upload-input"
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={bulkUploadLoading}
          />
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="card-header" style={{ borderBottom: '1px solid #e2e8f0', padding: '16px 20px' }}>
          <h3 style={{ margin: 0 }}>All Recruiters</h3>
          <div className="filters" style={{ display: 'flex', gap: '8px' }}>
            <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
              <input
                type="text"
                className="form-input"
                placeholder="🔍 Search by name, email, or code..."
                value={filters.search}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, search: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                style={{ paddingLeft: '36px' }}
              />
              <svg style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', width: '16px', height: '16px', pointerEvents: 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
            </div>
            <div style={{ position: 'relative', minWidth: '200px' }} className="county-filter-dropdown">
              <div
                onClick={() => setShowCountyDropdown(!showCountyDropdown)}
                className="form-select"
                style={{ color: filters.county ? '#1e293b' : '#94a3b8' }}
              >
                <span>{filters.county || '📍 All Counties'}</span>
                <svg style={{ width: '14px', height: '14px', transition: 'transform 0.2s', transform: showCountyDropdown ? 'rotate(180deg)' : 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/></svg>
              </div>
              {showCountyDropdown && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: '4px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '280px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ padding: '8px', borderBottom: '1px solid #e2e8f0' }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Search counties..."
                      value={countyFilterSearch}
                      onChange={(e) => setCountyFilterSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div style={{ overflowY: 'auto', flex: 1 }}>
                    <div
                      onClick={() => { handleCountyFilterSelect(''); setCountyFilterSearch(''); }}
                      style={{ padding: '10px 12px', cursor: 'pointer', fontSize: '14px', background: !filters.county ? '#f0f9ff' : 'transparent', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}
                    >
                      All Counties
                    </div>
                    {filteredCountiesForFilter.map((county) => (
                      <div
                        key={county.code}
                        onClick={() => { handleCountyFilterSelect(county.name); setCountyFilterSearch(''); }}
                        style={{ padding: '10px 12px', cursor: 'pointer', fontSize: '14px', background: filters.county === county.name ? '#f0f9ff' : 'transparent', borderBottom: '1px solid #f1f5f9' }}
                        onMouseEnter={(e) => e.target.style.background = filters.county === county.name ? '#f0f9ff' : '#f8fafc'}
                        onMouseLeave={(e) => e.target.style.background = filters.county === county.name ? '#f0f9ff' : 'transparent'}
                      >
                        {county.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div style={{ position: 'relative', minWidth: '160px' }}>
              <div
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className="form-select"
                style={{ color: filters.category ? '#1e293b' : '#94a3b8' }}
              >
                <span>{filters.category ? (categories.find(c => c.name === filters.category)?.display_name || filters.category) : 'All Categories'}</span>
                <svg style={{ width: '14px', height: '14px', transition: 'transform 0.2s', transform: showCategoryDropdown ? 'rotate(180deg)' : 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/></svg>
              </div>
              {showCategoryDropdown && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: '4px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                  <div
                    onClick={() => { handleCategoryFilterSelect(''); setShowCategoryDropdown(false); }}
                    style={{ padding: '10px 12px', cursor: 'pointer', fontSize: '14px', background: !filters.category ? '#f0f9ff' : 'transparent', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}
                  >
                    All Categories
                  </div>
                  {categories.map((cat) => (
                    <div
                      key={cat.id}
                      onClick={() => { handleCategoryFilterSelect(cat.name); setShowCategoryDropdown(false); }}
                      style={{ padding: '10px 12px', cursor: 'pointer', fontSize: '14px', background: filters.category === cat.name ? '#f0f9ff' : 'transparent', borderBottom: '1px solid #f1f5f9' }}
                      onMouseEnter={(e) => e.target.style.background = filters.category === cat.name ? '#f0f9ff' : '#f8fafc'}
                      onMouseLeave={(e) => e.target.style.background = filters.category === cat.name ? '#f0f9ff' : 'transparent'}
                    >
                      {cat.display_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bulk Upload Instructions */}
        <div className="card" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', margin: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '18px' }}>💡</div>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px', color: '#1f2937' }}>Bulk Upload Instructions</h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                1. Click "📥 Download Template" to get the CSV format •
                2. Fill in recruiter information •
                3. Save as CSV •
                4. Use "📤 Bulk Upload" to choose your file •
                5. Review the preview and click "🚀 Submit & Process"
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: '12px', padding: '4px 8px', backgroundColor: '#10b981', color: 'white', borderRadius: '4px', fontWeight: '600' }}>CSV Format</span>
              <span style={{ fontSize: '12px', padding: '4px 8px', backgroundColor: '#f59e0b', color: 'white', borderRadius: '4px', fontWeight: '600' }}>15 Fields</span>
            </div>
          </div>
        </div>

        {/* Bulk Upload Progress */}
        {(bulkUploadLoading || uploadProgress.status) && (
          <div className="card" style={{ margin: '16px 20px', backgroundColor: bulkJobDone ? '#f0fdf4' : '#f0f9ff', border: `1px solid ${bulkJobDone ? '#22c55e' : '#0ea5e9'}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: bulkJobDone ? '#22c55e' : bulkUploadLoading ? '#3b82f6' : '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '20px', flexShrink: 0 }}>
                {bulkJobDone ? '✅' : bulkUploadLoading ? '⏳' : '📧'}
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px', color: '#1f2937' }}>
                  {bulkJobDone ? 'All Done!' : bulkUploadLoading ? 'Uploading & Inserting Records...' : 'Sending Welcome Emails in Background'}
                </h3>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px' }}>{uploadProgress.status}</p>
                {uploadProgress.total > 0 && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      <span>📧 Emails sent: {uploadProgress.current} / {uploadProgress.total}</span>
                      <span>{Math.round((uploadProgress.current / uploadProgress.total) * 100)}%</span>
                    </div>
                    <div style={{ width: '100%', height: '10px', backgroundColor: '#e5e7eb', borderRadius: '5px', overflow: 'hidden' }}>
                      <div style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%`, height: '100%', background: bulkJobDone ? 'linear-gradient(90deg, #22c55e, #16a34a)' : 'linear-gradient(90deg, #3b82f6, #0ea5e9)', borderRadius: '5px', transition: 'width 0.5s ease' }} />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* File Preview and Submit */}
        {selectedFile && (
          <div className="card" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', margin: '16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '18px' }}>📁</div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: '#1f2937' }}>File Selected: {selectedFile.name}</h3>
                <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
                  Size: {(selectedFile.size / 1024).toFixed(2)} KB • Type: {selectedFile.name.split('.').pop().toUpperCase()}
                </p>
                {filePreview && (
                  <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>File Preview:</h4>
                    {filePreview.type === 'csv' ? (
                      <div>
                        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Showing first 5 data rows (Total: {filePreview.totalLines} lines)</p>
                        <div style={{ fontFamily: 'monospace', fontSize: '12px', backgroundColor: '#f9fafb', padding: '8px', borderRadius: '4px', overflow: 'auto', maxHeight: '150px' }}>
                          {filePreview.lines.map((line, index) => (
                            <div key={index} style={{ marginBottom: '2px', color: index === 0 ? '#059669' : '#374151', fontWeight: index === 0 ? '600' : '400' }}>
                              {line}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p style={{ fontSize: '12px', color: '#6b7280' }}>Excel file ready for processing. Preview not available for Excel files.</p>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    className={`btn btn-primary btn-sm ${bulkUploadLoading ? 'loading' : ''}`}
                    onClick={handleBulkSubmit}
                    disabled={bulkUploadLoading}
                    style={{
                      padding: '8px 20px', borderRadius: '8px', border: 'none',
                      background: bulkUploadLoading ? '#94a3b8' : '#037b90', color: '#fff',
                      fontSize: '14px', fontWeight: 500, cursor: bulkUploadLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {bulkUploadLoading ? '⏳ Processing...' : '🚀 Submit & Process'}
                  </button>
                  <button
                    onClick={handleClearFile}
                    disabled={bulkUploadLoading}
                    style={{
                      padding: '8px 20px', borderRadius: '8px',
                      border: '1px solid #d1d5db', background: '#fff', color: '#374151',
                      fontSize: '14px', fontWeight: 500, cursor: 'pointer'
                    }}
                  >
                    🗑️ Clear File
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card-body" style={{ padding: 0 }}>
          {recruiters.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <svg style={{ width: '48px', height: '48px', color: '#cbd5e1', marginBottom: '12px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
              <p style={{ margin: 0, color: '#94a3b8', fontSize: '15px' }}>No recruiters found</p>
              <p style={{ margin: '4px 0 0', color: '#cbd5e1', fontSize: '13px' }}>Try adjusting your search or filters</p>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table" style={{ margin: 0, border: 'none', width: '100%' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>Recruiter</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>Referral Code</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>Category</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>County</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>Status</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>Admissions</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', borderBottom: '1px solid #e2e8f0', textAlign: 'right' }}>Earnings</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', borderBottom: '1px solid #e2e8f0', textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recruiters.map((recruiter, index) => (
                      <tr
                        key={recruiter.id}
                        style={{
                          background: index % 2 === 0 ? '#ffffff' : '#fafbfc',
                          transition: 'background 0.15s',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                        onMouseLeave={(e) => e.currentTarget.style.background = index % 2 === 0 ? '#ffffff' : '#fafbfc'}
                        onClick={() => navigate(`/hr/recruiters/${recruiter.id}`)}
                      >
                        <td style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                              width: '36px', height: '36px', borderRadius: '50%',
                              background: '#037b90', color: '#fff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontWeight: 600, fontSize: '14px', flexShrink: 0
                            }}>
                              {(recruiter.full_name || '?').charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontWeight: 500, color: '#1e293b', fontSize: '14px' }}>{recruiter.full_name}</div>
                              <div style={{ color: '#94a3b8', fontSize: '13px' }}>{recruiter.email}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9' }}>
                          <code style={{ background: '#f1f5f9', padding: '3px 8px', borderRadius: '4px', fontSize: '13px', color: '#475569' }}>{recruiter.referral_code}</code>
                        </td>
                        <td style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', color: '#475569', fontSize: '14px' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500,
                            background: recruiter.category_name === 'student' ? '#dbeafe' : recruiter.category_name === 'external' ? '#fef3c7' : recruiter.category_name === 'alumni' ? '#e0f2fe' : '#f1f5f9',
                            color: recruiter.category_name === 'student' ? '#1e40af' : recruiter.category_name === 'external' ? '#92400e' : recruiter.category_name === 'alumni' ? '#0369a1' : '#475569'
                          }}>
                            {recruiter.category_display_name || recruiter.category_name || 'Uncategorized'}
                          </span>
                        </td>
                        <td style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', color: '#475569', fontSize: '14px' }}>{recruiter.county}</td>
                        <td style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 500,
                            background: recruiter.status === 'active' ? '#dcfce7' : recruiter.status === 'inactive' ? '#fef3c7' : '#fee2e2',
                            color: recruiter.status === 'active' ? '#166534' : recruiter.status === 'inactive' ? '#92400e' : '#991b1b'
                          }}>
                            <span style={{
                              width: '6px', height: '6px', borderRadius: '50%',
                              background: recruiter.status === 'active' ? '#16a34a' : recruiter.status === 'inactive' ? '#f59e0b' : '#dc2626',
                              display: 'inline-block'
                            }}/>
                            {recruiter.status}
                          </span>
                        </td>
                        <td style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            minWidth: '28px', height: '28px', borderRadius: '6px',
                            background: recruiter.total_admissions > 0 ? '#eff6ff' : '#f1f5f9',
                            color: recruiter.total_admissions > 0 ? '#037b90' : '#94a3b8',
                            fontWeight: 600, fontSize: '14px'
                          }}>
                            {recruiter.total_admissions || 0}
                          </span>
                        </td>
                        <td style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 500, color: '#1e293b', fontSize: '14px' }}>
                          KES {Number(recruiter.total_earned || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                            <button
                              className="btn btn-sm"
                              onClick={(e) => { e.stopPropagation(); handleView(recruiter); }}
                              style={{
                                padding: '6px 14px', fontSize: '13px', borderRadius: '6px',
                                border: '1px solid #e2e8f0', background: '#fff', color: '#475569',
                                cursor: 'pointer', fontWeight: 500
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                              onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                            >
                              View
                            </button>
                            <button
                              className="btn btn-sm"
                              onClick={(e) => { e.stopPropagation(); handleDelete(recruiter.id); }}
                              style={{
                                padding: '6px 14px', fontSize: '13px', borderRadius: '6px',
                                border: '1px solid #fecaca', background: '#fff', color: '#dc2626',
                                cursor: 'pointer', fontWeight: 500
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                              onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Pagination
                  pagination={pagination}
                  currentPage={pagination.page}
                  setCurrentPage={(page) => setPagination(prev => ({ ...prev, page }))}
                  loading={loading}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3>Add Recruiter</h3>
              <button className="modal-close" onClick={() => setShowForm(false)}>×</button>
            </div>
            <div className="modal-body">
              <HRRecruiterForm onSubmit={handleFormSubmit} onCancel={() => setShowForm(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HRRecruiterForm({ onSubmit, onCancel, initialData }) {
  const [formData, setFormData] = useState(initialData || {
    full_name: '',
    nationality: '',
    id_passport: '',
    kra_pin: '',
    county: '',
    subcounty: '',
    location: '',
    sublocation: '',
    village: '',
    mobile_number: '',
    email: '',
    residential_address: '',
    postal_address: '',
    bank_name: '',
    branch_code: '',
    bank_account: '',
    category_id: ''
  });
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [emailValidation, setEmailValidation] = useState({ isValid: true, errors: [] });

  useEffect(() => {
    apiCall('/recruiters/categories').then(res => res.json()).then(setCategories).catch(() => {});
  }, []);

  // Dropdown state
  const [nationalitySearch, setNationalitySearch] = useState('');
  const [showNationalityDropdown, setShowNationalityDropdown] = useState(false);
  const [countySearch, setCountySearch] = useState('');
  const [showCountyDropdown, setShowCountyDropdown] = useState(false);
  const [bankSearch, setBankSearch] = useState('');
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [branchSearch, setBranchSearch] = useState('');
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);



  const selectedBank = bankBranchData.find(b => b.bank_name === formData.bank_name);
  const availableBranches = selectedBank ? selectedBank.branches : [];

  const filteredCountries = countriesData.filter(country =>
    country.name.toLowerCase().includes(nationalitySearch.toLowerCase())
  );

  const filteredCounties = kenyanCountiesData.filter(county =>
    county.name.toLowerCase().includes(countySearch.toLowerCase())
  );

  const getSubcountiesForCounty = (countyName) => {
    const county = kenyanCountiesData.find(c => c.name === countyName);
    return county ? county.subCounties : [];
  };

  const filteredBanks = bankBranchData.filter(bank =>
    bank.bank_name.toLowerCase().includes(bankSearch.toLowerCase())
  );

  const filteredBranches = availableBranches.filter(branch =>
    branch.branch_name.toLowerCase().includes(branchSearch.toLowerCase()) ||
    branch.branch_code.includes(branchSearch)
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNationalitySelect = (nationality) => {
    setFormData(prev => ({ ...prev, nationality }));
    setNationalitySearch(nationality);
    setShowNationalityDropdown(false);
  };

  const handleCountySelect = (countyName) => {
    setFormData(prev => ({ ...prev, county: countyName, subcounty: '' }));
    setCountySearch(countyName);
    setShowCountyDropdown(false);
  };

  const handleBankSelect = (bankName) => {
    setFormData({ ...formData, bank_name: bankName, branch_code: '' });
    setBankSearch(bankName);
    setShowBankDropdown(false);
    setBranchSearch('');
  };

  const handleBranchSelect = (branchCode, branchName) => {
    setFormData({ ...formData, branch_code: branchCode });
    setBranchSearch(`${branchCode} - ${branchName}`);
    setShowBranchDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await onSubmit(formData);
    } catch (error) {
      toast.error('Failed to create recruiter');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="form">
      <div className="form-section">
        <h2>Personal Information</h2>
        <div className="form-row">
          <div className="form-group">
            <label>Full Name *</label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              className="form-control"
              required
            />
          </div>
          <div className="form-group">
            <label>Email *</label>
            {initialData ? (
              <input type="email" value={formData.email} className="form-control" disabled />
            ) : (
              <EmailInput
                value={formData.email}
                onChange={(value, validation) => {
                  setFormData(prev => ({ ...prev, email: value }));
                  setEmailValidation(validation);
                }}
                required
              />
            )}
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Mobile Number *</label>
            <input
              type="text"
              name="mobile_number"
              value={formData.mobile_number}
              onChange={handleChange}
              className="form-control"
              placeholder="+254..."
              required
            />
          </div>
          <div className="form-group">
            <label>Nationality *</label>
            <div style={{ position: 'relative' }}>
              <div
                onClick={() => setShowNationalityDropdown(!showNationalityDropdown)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '10px 12px', border: '1px solid #e2e8f0',
                  borderRadius: '6px', cursor: 'pointer', minWidth: '180px',
                  background: '#fff', fontSize: '14px', color: formData.nationality ? '#1e293b' : '#94a3b8',
                  justifyContent: 'space-between'
                }}
              >
                <span>{formData.nationality ? countriesData.find(c => c.name === formData.nationality)?.flag + ' ' + formData.nationality : 'Select Nationality'}</span>
                <svg style={{ width: '14px', height: '14px', color: '#94a3b8', transition: 'transform 0.2s', transform: showNationalityDropdown ? 'rotate(180deg)' : 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/></svg>
              </div>
              {showNationalityDropdown && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: '4px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '280px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ padding: '8px', borderBottom: '1px solid #e2e8f0' }}>
                    <input
                      type="text"
                      placeholder="Search countries..."
                      value={nationalitySearch}
                      onChange={(e) => setNationalitySearch(e.target.value)}
                      style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                      autoFocus
                    />
                  </div>
                  <div style={{ overflowY: 'auto', flex: 1 }}>
                    {filteredCountries.map((country) => (
                      <div
                        key={country.code}
                        onClick={() => { handleNationalitySelect(country.name); setNationalitySearch(''); }}
                        style={{ padding: '10px 12px', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', background: formData.nationality === country.name ? '#f0f9ff' : 'transparent', borderBottom: '1px solid #f1f5f9' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = formData.nationality === country.name ? '#f0f9ff' : '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.background = formData.nationality === country.name ? '#f0f9ff' : 'transparent'}
                      >
                        <span>{country.flag}</span>
                        <span>{country.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>ID/Passport *</label>
            <input
              type="text"
              name="id_passport"
              value={formData.id_passport}
              onChange={handleChange}
              className="form-control"
              required
            />
          </div>
          <div className="form-group">
            <label>KRA PIN</label>
            <input
              type="text"
              name="kra_pin"
              value={formData.kra_pin}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Category</label>
            <select
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              className="form-control"
            >
              <option value="">Auto (based on email domain)</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.display_name}</option>
              ))}
            </select>
          </div>
          <div className="form-group" />
        </div>
      </div>

      <div className="form-section">
        <h2>Location Information</h2>
        <div className="form-row">
          <div className="form-group">
            <label>County *</label>
            <select
              name="county"
              value={formData.county}
              onChange={handleChange}
              className="form-control"
              required
            >
              <option value="">Select County</option>
              {kenyanCountiesData.map(county => (
                <option key={county.code} value={county.name}>{county.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Subcounty</label>
            <select
              name="subcounty"
              value={formData.subcounty}
              onChange={handleChange}
              className="form-control"
            >
              <option value="">Select Subcounty</option>
              {getSubcountiesForCounty(formData.county).map(sub => (
                <option key={sub} value={sub}>{sub}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Location</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>Sublocation</label>
            <input
              type="text"
              name="sublocation"
              value={formData.sublocation}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Village</label>
            <input
              type="text"
              name="village"
              value={formData.village}
              onChange={handleChange}
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>Postal Address</label>
            <input
              type="text"
              name="postal_address"
              value={formData.postal_address}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        </div>
        <div className="form-group">
          <label>Residential Address</label>
          <textarea
            name="residential_address"
            value={formData.residential_address}
            onChange={handleChange}
            className="form-control"
            rows="3"
          />
        </div>
      </div>

      <div className="form-section">
        <h2>Payment Information</h2>
        <div className="form-row">
          <div className="form-group">
            <label>Bank Account *</label>
            <input
              type="text"
              name="bank_account"
              value={formData.bank_account}
              onChange={handleChange}
              className="form-control"
              required
            />
          </div>
          <div className="form-group">
            <label>Bank Name</label>
            <select
              name="bank_name"
              value={formData.bank_name}
              onChange={handleChange}
              className="form-control"
            >
              <option value="">Select Bank</option>
              {bankBranchData.map(bank => (
                <option key={bank.bank_name} value={bank.bank_name}>{bank.bank_name}</option>
              ))}
            </select>
          </div>
        </div>
        {formData.bank_name && (
          <div className="form-row">
            <div className="form-group">
              <label>Branch Code</label>
              <select
                name="branch_code"
                value={formData.branch_code}
                onChange={handleChange}
                className="form-control"
              >
                <option value="">Select Branch</option>
                {availableBranches.map(branch => (
                  <option key={branch.branch_code} value={branch.branch_code}>
                    {branch.branch_code} - {branch.branch_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? (initialData ? 'Updating...' : 'Creating...') : (initialData ? 'Update Recruiter' : 'Create Recruiter')}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function RecruiterDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recruiter, setRecruiter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('students');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    loadRecruiter();
  }, [id]);

  const loadRecruiter = async () => {
    try {
      const response = await hrAPI.getRecruiter(id);
      setRecruiter(response.data);
      setEditData(response.data);
    } catch (error) {
      toast.error(error.message || 'Failed to load recruiter');
      console.error('Load recruiter error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (formData) => {
    try {
      await hrAPI.updateRecruiter(id, formData);
      toast.success('Recruiter updated successfully');
      setShowEditModal(false);
      loadRecruiter();
    } catch (error) {
      toast.error('Failed to update recruiter');
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <p>Loading recruiter details...</p>
        </div>
      </div>
    );
  }

  if (!recruiter) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <p>Recruiter not found</p>
          <Link to="/hr/recruiters" className="btn btn-primary">Back to Recruiters</Link>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'students':
        return <StudentsTab students={recruiter.students || []} />;
      case 'admissions':
        return <AdmissionsTab admissions={recruiter.admissions || []} />;
      case 'enrollments':
        return <EnrollmentsTab enrollments={recruiter.enrollments || []} />;
      case 'payments':
        return <PaymentsTab payments={recruiter.payments || []} />;
      default:
        return null;
    }
  };

  return (
    <div className="main-content">
      <div className="dashboard-header">
        <div>
          <h1>{recruiter.full_name}</h1>
          <p style={{ color: '#64748b', margin: 0 }}>
            Referral Code: {recruiter.referral_code} | 
            Email: {recruiter.email} | 
            Category: <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              padding: '1px 6px', borderRadius: '4px', fontSize: '12px', fontWeight: 500,
              background: recruiter.category_name === 'student' ? '#dbeafe' : recruiter.category_name === 'external' ? '#fef3c7' : recruiter.category_name === 'alumni' ? '#e0f2fe' : '#f1f5f9',
              color: recruiter.category_name === 'student' ? '#1e40af' : recruiter.category_name === 'external' ? '#92400e' : recruiter.category_name === 'alumni' ? '#0369a1' : '#475569'
            }}>{recruiter.category_display_name || recruiter.category_name || 'Uncategorized'}</span>
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-sm btn-outline" onClick={() => setShowQRModal(true)}>
            QR Verify
          </button>
          <button className="btn btn-sm btn-outline" onClick={() => setShowEditModal(true)}>
            Edit Recruiter
          </button>
          <button className="btn btn-secondary" onClick={() => navigate('/hr/recruiters')}>
            Back to Recruiters
          </button>
        </div>
      </div>

      <div className="dashboard-grid" style={{ marginBottom: '24px' }}>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center' }}>
            <h3 style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Total Admissions</h3>
            <p className="stat-value" style={{ margin: '8px 0 0' }}>{recruiter.total_admissions || 0}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center' }}>
            <h3 style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Total Enrollments</h3>
            <p className="stat-value" style={{ margin: '8px 0 0' }}>{recruiter.total_enrollments || 0}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center' }}>
            <h3 style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Total Earnings</h3>
            <p className="stat-value" style={{ margin: '8px 0 0' }}>KES {Number(recruiter.total_earned || 0).toFixed(2)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center' }}>
            <h3 style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Payment Status</h3>
            <p className="stat-value" style={{ margin: '8px 0 0' }}>
              <span className={`status-badge ${recruiter.payment_status || 'pending'}`}>
                {recruiter.payment_status || 'pending'}
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <h3>Personal Information</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Full Name</label>
              <p style={{ margin: '4px 0' }}>{recruiter.full_name}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Mobile Number</label>
              <p style={{ margin: '4px 0' }}>{recruiter.mobile_number}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Nationality</label>
              <p style={{ margin: '4px 0' }}>{recruiter.nationality}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>ID/Passport</label>
              <p style={{ margin: '4px 0' }}>{recruiter.id_passport}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>KRA PIN</label>
              <p style={{ margin: '4px 0' }}>{recruiter.kra_pin || 'Not provided'}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Status</label>
              <p style={{ margin: '4px 0' }}>
                <span className={`status-badge ${recruiter.status}`}>{recruiter.status}</span>
              </p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>County</label>
              <p style={{ margin: '4px 0' }}>{recruiter.county}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Subcounty</label>
              <p style={{ margin: '4px 0' }}>{recruiter.subcounty || 'Not provided'}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Residential Address</label>
              <p style={{ margin: '4px 0' }}>{recruiter.residential_address}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Bank Account</label>
              <p style={{ margin: '4px 0' }}>{recruiter.bank_account || 'Not provided'}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Bank Name</label>
              <p style={{ margin: '4px 0' }}>{recruiter.bank_name || 'Not provided'}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Branch Code</label>
              <p style={{ margin: '4px 0' }}>{recruiter.branch_code || 'Not provided'}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Referral Code</label>
              <p style={{ margin: '4px 0' }}>{recruiter.referral_code}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header" style={{ borderBottom: 'none', paddingBottom: 0 }}>
          <div className="tabs" style={{ display: 'flex', gap: '4px', borderBottom: '2px solid #e2e8f0', width: '100%' }}>
            {[
              { id: 'students', label: 'Students', count: (recruiter.students || []).length },
              { id: 'admissions', label: 'Admissions', count: (recruiter.admissions || []).length },
              { id: 'enrollments', label: 'Enrollments', count: (recruiter.enrollments || []).length },
              { id: 'payments', label: 'Payments', count: (recruiter.payments || []).length }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontWeight: activeTab === tab.id ? 600 : 400,
                  color: activeTab === tab.id ? '#037b90' : '#64748b',
                  borderBottom: activeTab === tab.id ? '2px solid #037b90' : '2px solid transparent',
                  marginBottom: '-2px',
                  fontSize: '14px'
                }}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>
        <div className="card-body" style={{ paddingTop: '20px' }}>
          {renderTabContent()}
        </div>
      </div>

      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3>Edit Recruiter</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <HRRecruiterForm onSubmit={handleUpdate} onCancel={() => setShowEditModal(false)} initialData={recruiter} />
            </div>
          </div>
        </div>
      )}

      {showQRModal && (
        <div className="modal-overlay" onClick={() => setShowQRModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3>Recruiter Verification QR</h3>
              <button className="modal-close" onClick={() => setShowQRModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center', padding: '32px' }}>
              <QRCodeSVG value={`${window.location.origin}/verify?code=${encodeURIComponent(recruiter.referral_code)}`} size={200} />
              <p style={{ margin: '16px 0 4px', fontWeight: 600, fontSize: '16px', color: '#1e293b' }}>{recruiter.full_name}</p>
              <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Code: {recruiter.referral_code}</p>
              <div style={{ marginTop: '16px', padding: '12px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#166534' }}>
                  Share this QR code with students to verify you are a genuine OUK recruiter.
                </p>
              </div>
              <button
                className="btn btn-primary"
                style={{ marginTop: '16px' }}
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/verify?code=${encodeURIComponent(recruiter.referral_code)}`);
                  toast.success('Verification link copied!');
                }}
              >
                Copy Verification Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StudentsTab({ students }) {
  if (students.length === 0) {
    return <p style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No students recruited yet</p>;
  }

  return (
    <div className="table-container">
    <table className="table">
      <thead>
        <tr>
          <th>Student Name</th>
          <th>Student ID</th>
          <th>Course</th>
          <th>Admission Date</th>
          <th>Verified</th>
          <th>Commission</th>
        </tr>
      </thead>
      <tbody>
        {students.map((student) => (
          <tr key={student.id}>
            <td>{student.student_name}</td>
            <td>{student.student_id}</td>
            <td>{student.course || 'N/A'}</td>
            <td>{student.admission_date ? new Date(student.admission_date).toLocaleDateString() : 'N/A'}</td>
            <td>
              <span className={`status-badge ${student.admission_verified ? 'active' : 'pending'}`}>
                {student.admission_verified ? 'Verified' : 'Pending'}
              </span>
            </td>
            <td>KES {Number(student.commission_amount || 0).toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function AdmissionsTab({ admissions }) {
  if (admissions.length === 0) {
    return <p style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No admissions recorded</p>;
  }

  return (
    <div className="table-container">
    <table className="table">
      <thead>
        <tr>
          <th>Student Name</th>
          <th>Student ID</th>
          <th>Admission Date</th>
          <th>Verified</th>
          <th>Commission</th>
        </tr>
      </thead>
      <tbody>
        {admissions.map((admission) => (
          <tr key={admission.id}>
            <td>{admission.student_name || 'N/A'}</td>
            <td>{admission.student_id}</td>
            <td>{new Date(admission.admission_date).toLocaleDateString()}</td>
            <td>
              <span className={`status-badge ${admission.admission_verified ? 'active' : 'pending'}`}>
                {admission.admission_verified ? 'Verified' : 'Pending'}
              </span>
            </td>
            <td>KES {Number(admission.commission_amount || 500).toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function EnrollmentsTab({ enrollments }) {
  if (enrollments.length === 0) {
    return <p style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No enrollments recorded</p>;
  }

  return (
    <div className="table-container">
    <table className="table">
      <thead>
        <tr>
          <th>Student Name</th>
          <th>Student ID</th>
          <th>Enrollment Date</th>
          <th>Verified</th>
          <th>Commission</th>
        </tr>
      </thead>
      <tbody>
        {enrollments.map((enrollment) => (
          <tr key={enrollment.id}>
            <td>{enrollment.student_name || 'N/A'}</td>
            <td>{enrollment.student_id}</td>
            <td>{new Date(enrollment.enrollment_date).toLocaleDateString()}</td>
            <td>
              <span className={`status-badge ${enrollment.enrollment_verified ? 'active' : 'pending'}`}>
                {enrollment.enrollment_verified ? 'Verified' : 'Pending'}
              </span>
            </td>
            <td>KES {Number(enrollment.commission_amount || 300).toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function PaymentsTab({ payments }) {
  if (payments.length === 0) {
    return <p style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No payment records yet</p>;
  }

  return (
    <div className="table-container">
    <table className="table">
      <thead>
        <tr>
          <th>Date</th>
          <th>Admissions</th>
          <th>Enrollments</th>
          <th>Total Amount</th>
          <th>Status</th>
          <th>Reference</th>
        </tr>
      </thead>
      <tbody>
        {payments.map((payment) => (
          <tr key={payment.id}>
            <td>{payment.created_at ? new Date(payment.created_at).toLocaleDateString() : 'N/A'}</td>
            <td>{payment.total_admissions || 0}</td>
            <td>{payment.total_enrollments || 0}</td>
            <td>KES {Number(payment.cumulative_amount || 0).toFixed(2)}</td>
            <td>
              <span className={`status-badge ${payment.payment_status || 'pending'}`}>
                {payment.payment_status || 'pending'}
              </span>
            </td>
            <td>{payment.payment_reference || 'N/A'}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function HRDashboardHome() {
  const [stats, setStats] = useState(null);
  const [recentApplications, setRecentApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await hrAPI.getStats();
      setStats(response.data.stats);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="dashboard-header">
        <h1>HR Dashboard</h1>
      </div>

      <div className="dashboard-grid">
        <div className="card">
          <div className="card-body">
            <h3>Total Applications</h3>
            <p className="stat-value">{stats?.total || 0}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <h3>Pending Review</h3>
            <p className="stat-value">{stats?.pending || 0}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <h3>Approved</h3>
            <p className="stat-value">{stats?.approved || 0}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <h3>Onboarded</h3>
            <p className="stat-value">{stats?.onboarded || 0}</p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Quick Actions</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <Link to="/hr/applications" className="btn btn-primary">View Applications</Link>
            <Link to="/hr/recruiters" className="btn btn-outline">Manage Recruiters</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Applications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10 });
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    loadApplications();
  }, [pagination.page, filters, showArchived]);

  const loadApplications = async () => {
    try {
      const response = await hrAPI.getApplications({
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
        show_archived: showArchived ? 'true' : 'false'
      });
      setApplications(response.data.applications);
      setPagination(prev => ({ ...prev, total: response.data.pagination.total }));
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) {
      return;
    }

    try {
      await hrAPI.deleteApplication(id);
      toast.success('Application deleted successfully');
      loadApplications();
    } catch (error) {
      toast.error('Failed to delete application');
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <p>Loading applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="dashboard-header">
        <h1>Recruiter Applications</h1>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>All Applications</h3>
          <div className="filters">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="form-select"
              style={{ minWidth: '150px' }}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="under_review">Under Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="onboarded">Onboarded</option>
            </select>
            <input
              type="text"
              className="form-input"
              placeholder="🔍 Search applications..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              style={{ minWidth: '250px' }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => { setShowArchived(e.target.checked); setPagination(p => ({ ...p, page: 1 })); }}
              />
              Show archived
            </label>
          </div>
        </div>
        <div className="card-body">
          {applications.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
              No applications found
            </p>
          ) : (
            <>
              <div className="table-container">
    <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>County</th>
                    <th>Status</th>
                    <th>Applied Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.map((app) => (
                    <tr key={app.id}>
                      <td>{app.full_name}</td>
                      <td>{app.email}</td>
                      <td>{app.mobile_number}</td>
                      <td>{app.county}</td>
                      <td>
                        <span className={`status-badge ${app.status}`}>
                          {app.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td>{new Date(app.created_at).toLocaleDateString()}</td>
                      <td>
                        <Link to={`/hr/applications/${app.id}`} className="btn btn-sm">
                          View
                        </Link>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(app.id)}
                          style={{ marginLeft: '5px' }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              <Pagination
                pagination={pagination}
                currentPage={pagination.page}
                setCurrentPage={(page) => setPagination(prev => ({ ...prev, page }))}
                loading={loading}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ApplicationDetails() {
  const { id } = useParams();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewData, setReviewData] = useState({ status: '', notes: '', rejection_reason: '' });
  const [showReviewModal, setShowReviewModal] = useState(false);

  useEffect(() => {
    loadApplication();
  }, [id]);

  const loadApplication = async () => {
    try {
      const response = await hrAPI.getApplication(id);
      setApplication(response.data);
    } catch (error) {
      toast.error('Failed to load application');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async () => {
    try {
      await hrAPI.reviewApplication(id, reviewData);
      toast.success('Application reviewed successfully');
      setShowReviewModal(false);
      loadApplication();
    } catch (error) {
      toast.error('Failed to review application');
    }
  };

  const handleOnboard = async () => {
    try {
      const response = await hrAPI.onboardRecruiter(id);
      toast.success(`Recruiter onboarded successfully! Referral: ${response.data?.referralCode || ''}`);
      loadApplication();
    } catch (error) {
      toast.error('Failed to onboard recruiter');
    }
  };

  const handleArchive = async (is_archived) => {
    try {
      await hrAPI.archiveApplication(id, is_archived);
      toast.success(`Application ${is_archived ? 'archived' : 'restored'} successfully`);
      loadApplication();
    } catch (error) {
      toast.error('Failed to archive application');
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <p>Loading application...</p>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <p>Application not found</p>
          <Link to="/hr/applications" className="btn btn-primary">Back to Applications</Link>
        </div>
      </div>
    );
  }

  const detailRow = (label, value) => (
    <div className="detail-row">
      <label>{label}:</label>
      <span>{value || 'Not provided'}</span>
    </div>
  );

  return (
    <div className="main-content">
      <div className="dashboard-header">
        <div>
          <h1>Application Details</h1>
          <p style={{ color: '#64748b', margin: 0 }}>{application.full_name}</p>
        </div>
        <Link to="/hr/applications" className="btn btn-secondary">← Back to Applications</Link>
      </div>

      <div className="dashboard-grid">
        <div className="card">
          <div className="card-header">
            <h3>Personal Information</h3>
          </div>
          <div className="card-body">
            {detailRow('Full Name', application.full_name)}
            {detailRow('Email', application.email)}
            {detailRow('Mobile Number', application.mobile_number)}
            {detailRow('Nationality', application.nationality)}
            {detailRow('ID/Passport', application.id_passport)}
            {detailRow('KRA PIN', application.kra_pin)}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Location Information</h3>
          </div>
          <div className="card-body">
            {detailRow('County', application.county)}
            {detailRow('Subcounty', application.subcounty)}
            {detailRow('Location', application.location)}
            {detailRow('Sublocation', application.sublocation)}
            {detailRow('Village', application.village)}
            {detailRow('Residential Address', application.residential_address)}
            {detailRow('Postal Address', application.postal_address)}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Payment Information</h3>
          </div>
          <div className="card-body">
            {detailRow('Bank Account', application.bank_account)}
            {detailRow('Bank Name', application.bank_name)}
            {detailRow('Branch Code', application.branch_code)}
            {detailRow('Mobile Money', application.mobile_money_number)}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Application Status</h3>
          </div>
          <div className="card-body">
            <div className="detail-row">
              <label>Status:</label>
              <span className={`status-badge ${application.status}`}>
                {application.status.replace('_', ' ')}
              </span>
            </div>
            {detailRow('Applied Date', application.created_at ? new Date(application.created_at).toLocaleDateString() : '')}
            {detailRow('Review Notes', application.notes || application.review_notes)}
            {detailRow('Rejection Reason', application.rejection_reason)}
          </div>
        </div>
      </div>

      {application.cover_letter && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="card-header">
            <h3>Cover Letter</h3>
          </div>
          <div className="card-body">
            <p style={{ whiteSpace: 'pre-wrap', color: '#334155', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
              {application.cover_letter}
            </p>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3>Actions</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {application.status === 'pending' && (
              <button className="btn btn-primary" onClick={() => setShowReviewModal(true)}>
                Review Application
              </button>
            )}
            {application.status === 'under_review' && (
              <button className="btn btn-primary" onClick={() => setShowReviewModal(true)}>
                Update Review
              </button>
            )}
            {application.status === 'approved' && (
              <button className="btn btn-success" onClick={handleOnboard}>
                Onboard Recruiter
              </button>
            )}
            {application.status === 'rejected' && (
              <button className="btn btn-primary" onClick={() => setShowReviewModal(true)}>
                Reconsider Application
              </button>
            )}
            {(application.status === 'onboarded' || application.status === 'rejected') && (
              application.is_archived ? (
                <button className="btn btn-secondary" onClick={() => handleArchive(false)}>
                  Unarchive
                </button>
              ) : (
                <button className="btn btn-secondary" onClick={() => handleArchive(true)}>
                  Archive
                </button>
              )
            )}
          </div>
        </div>
      </div>

      {showReviewModal && (
        <div className="modal-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Review Application</h3>
              <button className="modal-close" onClick={() => setShowReviewModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label style={{ fontWeight: 600, color: '#374151', fontSize: '14px', marginBottom: '6px', display: 'block' }}>Status *</label>
                <select
                  value={reviewData.status}
                  onChange={(e) => setReviewData({ ...reviewData, status: e.target.value })}
                  className="form-select"
                  style={{ width: '100%' }}
                >
                  <option value="">Select Status</option>
                  <option value="under_review">Under Review</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label style={{ fontWeight: 600, color: '#374151', fontSize: '14px', marginBottom: '6px', display: 'block' }}>Review Notes</label>
                <textarea
                  value={reviewData.notes}
                  onChange={(e) => setReviewData({ ...reviewData, notes: e.target.value })}
                  className="form-input"
                  rows="4"
                  placeholder="Enter your review notes..."
                  style={{ width: '100%' }}
                />
              </div>
              {reviewData.status === 'rejected' && (
                <div className="form-group" style={{ marginTop: '16px' }}>
                  <label style={{ fontWeight: 600, color: '#dc2626', fontSize: '14px', marginBottom: '6px', display: 'block' }}>Rejection Reason *</label>
                  <textarea
                    value={reviewData.rejection_reason}
                    onChange={(e) => setReviewData({ ...reviewData, rejection_reason: e.target.value })}
                    className="form-input"
                    rows="3"
                    placeholder="Explain why this application is being rejected..."
                    style={{ width: '100%', borderColor: '#fca5a5' }}
                  />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowReviewModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleReview}
                disabled={!reviewData.status}
              >
                Submit Review
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HRDashboard;
